import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { useAsignarClientesMasivoMutation, useAsignacionProgress } from '../../services/hooks/useEventos';
import { useActivePlazasQuery } from '../../services/hooks/usePlazas';
import { UsersIcon, AlertTriangle, X } from 'lucide-react';
import { formatDateToLocal } from '../../utils/utils';

interface AsignarClientesMasivosProps {
    onClose: () => void;
}

const AsignarClientesMasivos: React.FC<AsignarClientesMasivosProps> = ({ onClose }) => {
    const [sessionId, setSessionId] = useState<string | undefined>();
    const [systemReady, setSystemReady] = useState<boolean | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    
    // Estados para las fechas
    const [fechaInicio, setFechaInicio] = useState<Date | null>(null);
    const [fechaFin, setFechaFin] = useState<Date | null>(null);

    // Consultar plazas activas
    const { data: plazas, isLoading: plazasLoading } = useActivePlazasQuery();
    
    // Verificar si hay plazas para asignar
    useEffect(() => {
        if (plazasLoading) return;
        
        if (!plazas || plazas.length === 0) {
            setSystemReady(false);
            setErrorMessage('No hay plazas activas para asignar a eventos.');
            return;
        }
        
        setSystemReady(true);
        setErrorMessage(null);
    }, [plazas, plazasLoading]);

    const { data: progress } = useAsignacionProgress(sessionId);
    const asignarClientes = useAsignarClientesMasivoMutation();
    
    const handleAsignacion = async () => {
        // Validar que las fechas estén seleccionadas
        if (!fechaInicio || !fechaFin) {
            toast.error('Por favor seleccione las fechas de inicio y fin');
            return;
        }

        // Validar que la fecha de inicio sea menor a la de fin
        if (fechaInicio >= fechaFin) {
            toast.error('La fecha de inicio debe ser menor a la fecha final');
            return;
        }

        try {
            const response = await asignarClientes.mutateAsync({
                fechaInicio: formatDateToLocal(fechaInicio),
                fechaFin: formatDateToLocal(fechaFin)
            });
            setSessionId(response.sessionId);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al iniciar la asignación');
        }
    };
    
    const handleClose = () => {
        onClose();
    };
    
    return (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative modal-content bg-white rounded-lg p-6 w-full max-w-md">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Asignar Clientes a Eventos</h2>
                </div>

                {systemReady === null && (
                    <div className="flex items-center justify-center p-6">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                        <span className="ml-2">Verificando plazas disponibles...</span>
                    </div>
                )}

                {systemReady === false && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex">
                            <AlertTriangle className="text-yellow-500 h-6 w-6 mr-2" />
                            <div>
                                <p className="text-sm text-yellow-700">
                                    {errorMessage}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {!sessionId && systemReady && (
                    <div className="mb-6">
                        <p className="mb-4">
                            Este proceso asignará automáticamente todos los clientes activos a 
                            los eventos correspondientes según sus plazas reservadas.
                        </p>
                        <div className="text-sm text-gray-600 mb-4">
                            <p>• Se encontraron {plazas?.length} plazas activas para asignar</p>
                        </div>
                        
                        {/* Sección de selección de fechas */}
                        <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                            <h3 className="text-lg font-bold mb-4">Seleccionar rango de fechas <span className="text-red-500">*</span></h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Fecha Inicio <span className="text-red-500">*</span>
                                    </label>
                                    <DatePicker
                                        selected={fechaInicio}
                                        onChange={(date) => setFechaInicio(date)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        placeholderText="Seleccione fecha inicio"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Fecha Fin <span className="text-red-500">*</span>
                                    </label>
                                    <DatePicker
                                        selected={fechaFin}
                                        onChange={(date) => setFechaFin(date)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        placeholderText="Seleccione fecha fin"
                                        minDate={fechaInicio || undefined}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <button
                            onClick={handleAsignacion}
                            disabled={asignarClientes.status === 'pending' || !fechaInicio || !fechaFin}
                            className={`w-full py-2 px-4 rounded flex items-center justify-center gap-2 ${
                                asignarClientes.status === 'pending' || !fechaInicio || !fechaFin
                                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                            <UsersIcon size={18} />
                            {asignarClientes.status === 'pending' ? 'Iniciando...' : 'Iniciar Asignación'}
                        </button>
                    </div>
                )}

                {sessionId && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-center">
                            {progress?.status === 'processing' && (
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                            )}
                            {progress?.status === 'completed' && (
                                <div className="text-green-500">
                                    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                            {progress?.status === 'error' && (
                                <div className="text-red-500">
                                    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                            )}
                        </div>

                        <div className="text-center">
                            <h3 className="text-lg font-medium">
                                {progress?.status === 'completed' 
                                    ? 'Asignación completada'
                                    : progress?.status === 'error'
                                    ? 'Error en la asignación'
                                    : 'Asignando clientes a eventos...'}
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-600">
                                    {progress?.processed || 0} de {progress?.total || 0} eventos procesados
                                </p>
                            </div>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${progress?.total ? (progress.processed / progress.total) * 100 : 0}%`
                                }}
                            />
                        </div>

                        {progress?.status === 'completed' && (
                            <button
                                onClick={handleClose}
                                className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
                            >
                                Cerrar
                            </button>
                        )}
                        
                        {progress?.status === 'error' && (
                            <div>
                                <p className="text-red-500 text-sm mb-2">
                                    {progress?.error || 'Ocurrió un error durante el proceso.'}
                                </p>
                                <button
                                    onClick={handleClose}
                                    className="w-full bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
                                >
                                    Cerrar
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AsignarClientesMasivos;